import { Button } from '@components/buttons';
import { IcoCheck, IcoCheckCircle, IcoInfo, IcoExternalLink, IcoX } from '@components/icons';
import { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { State, Lesson } from '@course/components/module-helpers';
import { getCurrentCourse, getSiblingLessons, lessonOpensAt } from './state';
import { LessonCompleteResult } from './celebration';
import { CelebrationPanel, celebrationHeading, SprintWeekCard } from './sprint-progress';
import { toHumanTime } from 'shared/dateutil';
import { URLS } from 'shared/urls';
import { showToast } from '@components/toaster';
import { useIntl } from 'shared/intl/use-intl';
import { Case } from '@components/conditional';
import { useCurrentTenant, useCurrentUser } from '@components/router/session-context';
import { useLocalStorageState } from 'client/lib/hooks';
import { useRouter } from '@components/router';
import { navigateUnguarded } from '@components/router/navigate-guards';
import { LastLessonLeaveGuard } from './last-lesson-guard';
import { isDoneForWeek, isLastLessonOfWeek } from './last-of-week';
import { showCompleteLessonError } from './complete-error';

interface Props {
  state: State;
  lesson: Lesson;
  nextDisabled: boolean;
  canManageCourse: boolean;
  setLessonComplete(lessonId: UUID, complete: boolean): Promise<LessonCompleteResult>;
}

function FooterLink({
  label,
  text,
  icon,
  type,
  isLoading,
  href,
  target,
  onClick,
  class: className,
}: {
  label: string;
  text: string;
  href?: string;
  isLoading?: boolean;
  icon?: ComponentChildren;
  target?: string;
  type: 'next' | 'prev';
  class?: string;
  onClick?: () => void;
}) {
  return (
    <Button
      class={`flex flex-col gap-1 justify-center p-4 rounded-md border ${
        type === 'next' ? 'items-end' : ''
      } ${
        className || 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-inherit dark:border-gray-700'
      }`}
      isLoading={isLoading}
      href={href}
      target={target}
      onClick={onClick}
    >
      <span class="block text-sm opacity-75">{label}</span>
      <strong class="grow flex gap-2 items-center">
        {type === 'prev' && <span>{icon}</span>}
        <span class="line-clamp-2">{text}</span>
        {type === 'next' && <span>{icon || '→'}</span>}
      </strong>
    </Button>
  );
}

function CompleteLessonButton({
  state,
  lesson,
  nextLesson,
  setLessonComplete,
}: Props & {
  nextLesson?: Lesson;
}) {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const course = getCurrentCourse(state);
  const currentUser = useCurrentUser()!;
  const router = useRouter();
  const nextOpensAt = nextLesson && lessonOpensAt(state, nextLesson);
  const nextLessonUrl =
    course && nextLesson
      ? URLS.student.lesson({
          course,
          lesson: nextLesson,
        })
      : undefined;
  // A coach let this student work ahead, and this lesson finishes everything
  // up to a module that has not opened yet: completing it opens that module.
  // The server decides; this only predicts it for the label.
  const opensNextModule =
    !!state.unlockOnComplete &&
    !!nextLesson &&
    !!nextOpensAt &&
    nextLesson.moduleId !== lesson.moduleId &&
    state.orderedLessonIds
      .slice(0, state.orderedLessonIds.indexOf(lesson.id))
      .every((id) => state.completedLessons.includes(id));

  async function markAsCompleted() {
    setIsLoading(true);
    try {
      // This odd ordering is to prevent a flicker when marking
      // as complete (e.g. the blue button becomes the split
      // mark incomplete, and green next button for a split second).
      // I think it's fine to navigiate first, since the odds
      // of an error are slim, and we'll show the error regardless.
      const { tier, unlockedModuleIds } = await setLessonComplete(lesson.id, true);
      const openedNext = !!nextLesson && !!unlockedModuleIds?.includes(nextLesson.moduleId);
      // The button promises to continue, so an open next lesson is always
      // where the click goes, celebrated with a toast. A locked one is never
      // landed on: the page stays and shows the celebration panel instead.
      if (nextLessonUrl && (!nextOpensAt || openedNext)) {
        if (tier) {
          showToast({
            type: 'ok',
            title: celebrationHeading(intl, tier, { state, lesson }),
            message: '',
          });
        }
        // Just marked complete, so leaving is exactly what the guard wants;
        // it has not re-rendered to find out yet.
        navigateUnguarded(() => router.goto(nextLessonUrl));
      } else if (!nextLessonUrl && course.hasEnabledCert) {
        window.open(URLS.student.certificate({ course, userId: currentUser.id }), '_blank');
      }
    } catch (err) {
      setIsLoading(false);
      showCompleteLessonError(intl, err);
    }
  }

  const overrideLabel = course.completeButtonLabel;
  const hasContinuation =
    (!!nextLesson && !nextOpensAt) || (!nextLesson && !!course.hasEnabledCert);

  if (opensNextModule) {
    return (
      <FooterLink
        isLoading={isLoading}
        class="btn-primary border-transparent"
        onClick={markAsCompleted}
        label={intl('Mark complete and open next module')}
        type="next"
        text={nextLesson.title}
        icon={null}
      />
    );
  }

  return (
    <FooterLink
      isLoading={isLoading}
      class="btn-primary border-transparent"
      onClick={markAsCompleted}
      label={
        hasContinuation
          ? overrideLabel
            ? `${overrideLabel} — continue to`
            : intl('Mark as complete and continue to')
          : ''
      }
      type="next"
      text={
        nextLesson && !nextOpensAt
          ? nextLesson.title
          : !nextLesson && course.hasEnabledCert
            ? intl('Certificate of Completion')
            : overrideLabel || intl('Mark as complete.')
      }
      icon={
        nextLesson && !nextOpensAt ? null : !nextLesson && course.hasEnabledCert ? (
          <IcoExternalLink />
        ) : (
          <IcoCheck />
        )
      }
    />
  );
}

export function NextLessonButton(props: Props & { siblings: { next?: Lesson } }) {
  const { lesson, canManageCourse, state, siblings } = props;
  const intl = useIntl();
  const isComplete = lesson && state.completedLessons.includes(lesson.id);
  const isPreviewMembership = state.accessLevel === 'preview' || state.accessLevel === 'limited';
  const currentUser = useCurrentUser()!;

  if (!isPreviewMembership && !canManageCourse && !isComplete) {
    return <CompleteLessonButton {...props} nextLesson={siblings.next} />;
  }

  if (siblings.next) {
    const nextOpensAt = lessonOpensAt(props.state, siblings.next);
    const nextLessonUrl = URLS.student.lesson({
      course: getCurrentCourse(props.state),
      lesson: siblings.next,
    });

    return (
      <FooterLink
        href={nextLessonUrl}
        type="next"
        text={siblings.next.title}
        label={
          nextOpensAt
            ? intl('Opens {date:string}', { date: toHumanTime(nextOpensAt) })
            : intl(`Next lesson`)
        }
      />
    );
  }

  if (props.state.course.hasEnabledCert) {
    return (
      <FooterLink
        href={URLS.student.certificate({ course: props.state.course, userId: currentUser.id })}
        target="_blank"
        label={intl('Continue to')}
        text={intl('Certificate of Completion')}
        icon={<IcoExternalLink />}
        type="next"
      />
    );
  }

  return null;
}

function PrevLessonButton({
  siblings,
  ...props
}: Props & {
  siblings: { prev?: Lesson };
}) {
  const intl = useIntl();
  if (!siblings.prev) {
    return <span></span>;
  }

  const prevLessonUrl = URLS.student.lesson({
    course: getCurrentCourse(props.state),
    lesson: siblings.prev,
  });

  return (
    <FooterLink
      href={prevLessonUrl}
      label={intl('Previous')}
      text={siblings.prev.title}
      icon="⬸"
      type="prev"
    />
  );
}

function UncompleteButton(props: Props) {
  const intl = useIntl();
  const { lesson, setLessonComplete } = props;

  if (props.canManageCourse) {
    return null;
  }

  return (
    <div class="flex flex-wrap leading-8 items-center dark:text-gray-200 space-x-2 px-4 py-2 border dark:border-gray-700 rounded-md mb-6">
      <IcoCheckCircle class="w-6 h-6 opacity-75 text-green-400" />
      <strong>{intl('You completed this lesson.')}</strong>
      <Case when={lesson.assessmentType !== 'assignment' || !lesson.requireAssignmentApproval}>
        <span class="text-gray-500 dark:text-gray-400">{intl('Not finished?')}</span>
        <Button
          class="underline text-gray-500 dark:text-gray-400 dark:hover:text-white"
          onClick={() => setLessonComplete(lesson.id, false)}
        >
          {intl('Mark as incomplete.')}
        </Button>
      </Case>
    </div>
  );
}

function ViewingAsGuideBanner(props: Props) {
  const { terminology } = useCurrentTenant();
  const [isDismissed, setIsDismissed] = useLocalStorageState(
    `dismiss_complete_button_info_${props.state.courseId}`,
    false,
  );

  if (isDismissed) {
    return null;
  }

  const module = props.state.modules[props.lesson.moduleId];
  const completeButtonLabel =
    getCurrentCourse(props.state).completeButtonLabel || 'Mark as complete';

  return (
    <div class="flex justify-between dark:text-gray-200 space-x-2 px-4 py-3 border border-yellow-200 rounded-md mb-6 relative bg-yellow-50 dark:bg-yellow-900 dark:border-yellow-700">
      <div class="flex items-center gap-4">
        <IcoInfo class="size-6 text-yellow-600 dark:text-yellow-200" />
        {module.isDraft && (
          <span>
            This {terminology.module} is hidden. Students won't see any {terminology.lessons} in
            this {terminology.module} until you unhide it.
          </span>
        )}
        {!module.isDraft && (
          <span>
            You're viewing this as a {terminology.guide}. Students will see the{' '}
            <strong>{completeButtonLabel}</strong> button.
          </span>
        )}
      </div>
      <Button
        class="p-1 text-gray-500 dark:text-gray-300 hover:opacity-75"
        onClick={() => setIsDismissed(true)}
      >
        <IcoX />
      </Button>
    </div>
  );
}

export function LessonFooterNav(props: Props) {
  const intl = useIntl();
  const { lesson, state } = props;
  const { celebration } = state;
  const siblings = getSiblingLessons(state);
  // Mirrors CompleteLessonButton: an open next lesson is navigated to, so the
  // panel would only flash here before the page changes.
  const nextOpensAt = siblings.next && lessonOpensAt(state, siblings.next);
  const staysOnPage = !siblings.next || !!nextOpensAt;
  const isComplete = lesson && state.completedLessons.includes(lesson.id);
  const isPreviewMembership = state.accessLevel === 'preview' || state.accessLevel === 'limited';
  const course = getCurrentCourse(state);
  const showDeadlineText =
    !!course.certGateDeadlineText &&
    !!lesson &&
    !!course.certGateModuleIds?.includes(lesson.moduleId) &&
    !isComplete &&
    !isPreviewMembership &&
    !props.canManageCourse &&
    !props.nextDisabled;
  const isStudentView = !!lesson && !isPreviewMembership && !props.canManageCourse;
  const isOpenLastOfWeek = isStudentView && isLastLessonOfWeek(state.sprintWeek, lesson.id);
  const doneForWeek = isDoneForWeek({
    week: state.sprintWeek,
    lessonId: lesson?.id,
    isComplete: !!isComplete,
  });

  return (
    <footer class="py-8 dark:text-gray-300">
      {props.canManageCourse && <ViewingAsGuideBanner {...props} />}
      {isComplete && celebration?.lessonId === lesson.id && staysOnPage && (
        <>
          <CelebrationPanel
            tier={celebration.tier}
            lesson={lesson}
            state={state}
            nextOpensAt={nextOpensAt}
          />
          {state.sprintWeek && <SprintWeekCard course={course} week={state.sprintWeek} />}
        </>
      )}
      {isComplete && !isPreviewMembership && <UncompleteButton {...props} />}
      <div class="grid gap-4 sm:grid-cols-2">
        <PrevLessonButton {...props} siblings={siblings} />
        {!props.nextDisabled && <NextLessonButton {...props} siblings={siblings} />}
      </div>
      {isOpenLastOfWeek && !doneForWeek && (
        <p class="mt-3 text-sm text-center font-medium text-amber-700 dark:text-amber-400">
          {intl("Last lesson this week. Mark it complete when you've finished it.")}
        </p>
      )}
      {showDeadlineText && (
        <p class="mt-3 text-sm text-center text-gray-500 dark:text-gray-400">
          {course.certGateDeadlineText}
        </p>
      )}
      {/* nextDisabled: the page hides the complete button (quiz not passed,
          approval pending, prerequisites), so the dialog, which offers to
          complete, must not appear. The hint above still shows. */}
      {isOpenLastOfWeek && !props.nextDisabled && (
        <LastLessonLeaveGuard
          lessonId={lesson.id}
          isComplete={doneForWeek}
          setLessonComplete={props.setLessonComplete}
        />
      )}
    </footer>
  );
}
